import type { HubAccountWithBindings, IntegrationBinding } from "./api";

// Pure helpers for the Integrations Hub account lists (Boo tab, org tab,
// manage page, onboarding). Accounts have a scope: private to their home Boo,
// or shared with the workspace. The API already filters by scope; these
// helpers decide how a view splits and gates what it got.

// Query key for one accounts listing. The list differs per Boo (private
// accounts are only returned for their home), so the key carries the Boo;
// invalidate with HUB_ACCOUNTS_KEY to refresh every variant.
export const HUB_ACCOUNTS_KEY = ["hub-accounts"] as const;
export function hubAccountsKey(containerId?: string) {
  return [...HUB_ACCOUNTS_KEY, containerId ?? "workspace"] as const;
}

// Can this Boo use the account right now: bound to it, or bound org-wide.
export function usableByBoo(account: HubAccountWithBindings, containerId: string): boolean {
  return account.bindings.some((b) => b.containerId === null || b.containerId === containerId);
}

export interface BooAccountSplit {
  // Private to this Boo.
  own: HubAccountWithBindings[];
  // Workspace accounts, whether or not this Boo is bound to one.
  shared: HubAccountWithBindings[];
}

// Boo tab: what belongs to this Boo vs what the workspace offers it. A private
// account whose home is another Boo is dropped — the API shouldn't return
// one, and showing it would leak a colleague's connection.
export function partitionForBoo(
  accounts: HubAccountWithBindings[],
  containerId: string,
): BooAccountSplit {
  const own: HubAccountWithBindings[] = [];
  const shared: HubAccountWithBindings[] = [];
  for (const a of accounts) {
    if (a.scope === "workspace") shared.push(a);
    else if (a.homeContainerId === containerId) own.push(a);
  }
  return { own, shared };
}

// The bindings a viewer may edit (tool policy, purpose). Managing the account
// covers every binding; otherwise only bindings on Boos the viewer configures.
export function writableBindings(
  account: HubAccountWithBindings,
  configurable: ReadonlySet<string>,
): IntegrationBinding[] {
  if (account.canManage) return account.bindings;
  return account.bindings.filter((b) => b.containerId !== null && configurable.has(b.containerId));
}

// Binding and unbinding a Boo are not symmetric on the backend. Adding a
// binding to a Boo always needs configure on that Boo — there is no org-role
// path, so managing the account is not enough — and a private account only
// ever binds to its home. Removing one is allowed to whoever manages the
// account or configures the Boo.
export function canAddBinding(
  account: HubAccountWithBindings,
  containerId: string,
  configurable: ReadonlySet<string>,
): boolean {
  if (account.scope === "boo" && account.homeContainerId !== containerId) return false;
  return configurable.has(containerId);
}

export function canRemoveBinding(
  account: HubAccountWithBindings,
  containerId: string,
  configurable: ReadonlySet<string>,
): boolean {
  return account.canManage || configurable.has(containerId);
}

// What a Boo has to work with: its own connections plus the shared ones it is
// bound to. Drives the "N of 3 connected" progress on the Boo tab and in
// onboarding.
export function connectedForBoo(
  accounts: HubAccountWithBindings[],
  containerId: string,
): HubAccountWithBindings[] {
  const { own, shared } = partitionForBoo(accounts, containerId);
  return [...own, ...shared.filter((a) => usableByBoo(a, containerId))];
}
